import { useEffect, useRef, useState } from 'react';

import {
  getAtomMotionProfile,
  getFrameDelta,
  limitVelocity,
} from './gamePhysics';

// === 設定データ (カオス恋愛編 12種類完全版) ===
const ATOMS = {
  H: { sym: 'H', face: '🥺', type: 'cov', val: 1, c: '#ffffff', text: '#ff8fab', border: '#ffc2d1', r: 18, desc: '一途' },
  F: { sym: 'F', face: '😈', type: 'cov', val: 1, c: '#e2e8f0', text: '#475569', border: '#cbd5e1', r: 17, desc: '奪う愛(最強)' },
  Cl: { sym: 'Cl', face: '🔪', type: 'cov', val: 1, c: '#d8f3dc', text: '#40916c', border: '#b7e4c7', r: 21, desc: '地雷系(重い)' },
  Br: { sym: 'Br', face: '👿', type: 'cov', val: 1, c: '#f3e8ff', text: '#7e22ce', border: '#d8b4fe', r: 22, desc: '束縛系' },
  I: { sym: 'I', face: '🧐', type: 'cov', val: 1, c: '#e0e7ff', text: '#4338ca', border: '#c7d2fe', r: 24, desc: 'インテリ' },
  
  O: { sym: 'O', face: '😏', type: 'cov', val: 2, c: '#ffafcc', text: '#5c5470', border: '#ff8fab', r: 21, desc: '二股狙い' },
  S: { sym: 'S', face: '♨️', type: 'cov', val: 2, c: '#fef08a', text: '#a16207', border: '#fde047', r: 22, desc: '匂わせ' },
  
  N: { sym: 'N', face: '😎', type: 'cov', val: 3, c: '#a2d2ff', text: '#5c5470', border: '#89c2d9', r: 21, desc: '三股男' },
  P: { sym: 'P', face: '🔥', type: 'cov', val: 3, c: '#ffedd5', text: '#c2410c', border: '#fdba74', r: 22, desc: '熱血三股' },
  B: { sym: 'B', face: '👽', type: 'cov', val: 3, c: '#dcfce7', text: '#15803d', border: '#bbf7d0', r: 20, desc: '不思議ちゃん' },
  
  C: { sym: 'C', face: '👑', type: 'cov', val: 4, c: '#cbd5e1', text: '#334155', border: '#94a3b8', r: 24, desc: 'ハーレム王' },
  Si: { sym: 'Si', face: '💻', type: 'cov', val: 4, c: '#f1f5f9', text: '#475569', border: '#cbd5e1', r: 24, desc: 'IT系4股' },
};

const DROP_POOL = [
  'H','H','H','H','H','H','H',
  'F','F','Cl','Cl','Br','Br','I','I',
  'O','O','O','S','S',
  'N','N','P','B',
  'C','C','Si'
];

// === 電子式・構造式 レンダリングコンポーネント (大学入試基準に最適化) ===
const MolecularRenderer = ({ layout, type }) => {
  if (!layout) return null;
  const { atoms, bonds } = layout;

  return (
    <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-sm overflow-visible">
      {/* 結合の描画 */}
      {bonds.map((bond, i) => {
        const s = atoms[bond.s];
        const t = atoms[bond.t];
        const dx = t.x - s.x;
        const dy = t.y - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const nx = dx / dist;
        const ny = dy / dist;
        const px = -ny;
        const py = nx;
        const mx = s.x + dx / 2;
        const my = s.y + dy / 2;

        if (type === 'structural') {
          // テキストと重ならないように余白を取る
          const R1 = s.sym.length > 1 ? 14 : 12;
          const R2 = t.sym.length > 1 ? 14 : 12;
          const sx = s.x + nx * R1;
          const sy = s.y + ny * R1;
          const tx = t.x - nx * R2;
          const ty = t.y - ny * R2;

          const renderLine = (offset, key) => (
            <line key={key} x1={sx + px * offset} y1={sy + py * offset} x2={tx + px * offset} y2={ty + py * offset} stroke="#334155" strokeWidth="2" strokeLinecap="round" />
          );

          if (bond.o === 1) return renderLine(0, i);
          if (bond.o === 2) return <g key={i}>{renderLine(2.5, 1)}{renderLine(-2.5, 2)}</g>;
          if (bond.o === 3) return <g key={i}>{renderLine(0, 1)}{renderLine(4, 2)}{renderLine(-4, 3)}</g>;
        } else {
          // 電子式 (共有電子対: 黒色)
          const dots = [];
          
          if (bond.o === 1) {
            const gap = 2.5; 
            // 縦に2点並べる
            dots.push({ x: mx + px * gap, y: my + py * gap });
            dots.push({ x: mx - px * gap, y: my - py * gap });
          } else if (bond.o === 2) {
            const step = 2.5; // 結合方向のズレ
            const gap = 2.5;  // 垂直方向のズレ
            // 左列の縦2点
            dots.push({ x: mx - nx * step + px * gap, y: my - ny * step + py * gap });
            dots.push({ x: mx - nx * step - px * gap, y: my - ny * step - py * gap });
            // 右列の縦2点
            dots.push({ x: mx + nx * step + px * gap, y: my + ny * step + py * gap });
            dots.push({ x: mx + nx * step - px * gap, y: my + ny * step - py * gap });
          } else if (bond.o === 3) {
            const step = 2.5; // 結合方向のズレ
            const gap = 3.5;  // 垂直方向のズレ
            // 左列の縦3点 (三重結合の教科書表記)
            dots.push({ x: mx - nx * step + px * gap, y: my - ny * step + py * gap });
            dots.push({ x: mx - nx * step, y: my - ny * step }); // 中央
            dots.push({ x: mx - nx * step - px * gap, y: my - ny * step - py * gap });
            // 右列の縦3点
            dots.push({ x: mx + nx * step + px * gap, y: my + ny * step + py * gap });
            dots.push({ x: mx + nx * step, y: my + ny * step }); // 中央
            dots.push({ x: mx + nx * step - px * gap, y: my + ny * step - py * gap });
          }

          return (
            <g key={`bond_${i}`}>
              {dots.map((d, di) => <circle key={di} cx={d.x} cy={d.y} r="1.8" fill="#1e293b" />)}
            </g>
          );
        }
      })}

      {/* 原子のテキストと非共有電子対 */}
      {atoms.map((atom, i) => (
        <g key={`atom_${i}`}>
          <text x={atom.x} y={atom.y} fontSize={atom.sym.length > 1 ? "14" : "16"} fontWeight="900" fill="#1e293b" textAnchor="middle" alignmentBaseline="central" dominantBaseline="central">
            {atom.sym}
          </text>
          
          {type === 'electronDot' && atom.lp && atom.lp.map((ang, li) => {
            // 非共有電子対の描画 (共有電子対と区別するため赤色に)
            const rad = ang * Math.PI / 180;
            const cx = Math.cos(rad);
            const cy = Math.sin(rad);
            const px = -cy;
            const py = cx;
            const R = atom.sym.length > 1 ? 16 : 14; 
            const gap = 3.5; 

            return (
              <g key={`lp_${li}`}>
                <circle cx={atom.x + cx * R + px * gap} cy={atom.y + cy * R + py * gap} r="1.8" fill="#ef4444" />
                <circle cx={atom.x + cx * R - px * gap} cy={atom.y + cy * R - py * gap} r="1.8" fill="#ef4444" />
              </g>
            );
          })}
        </g>
      ))}
    </svg>
  );
};

// === 結合判定・図解データ辞書 (十字配置・教科書準拠) ===
const RECIPES = [
  { 
    id: 'H2', f: {'H':2}, score: 30, name: '水素', formula: 'H₂',
    rel: '純愛カップル 💝', exp: '手と手を取り合う純愛。一番軽やかで爽やかな関係。',
    seriousExp: '水素原子2個がそれぞれの不対電子を出し合い、1組の共有電子対を作って結合した無極性分子です。',
    layout: {
      atoms: [ { sym: 'H', x: 30, y: 50 }, { sym: 'H', x: 70, y: 50 } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  { 
    id: 'Cl2', f: {'Cl':2}, score: 40, name: '塩素', formula: 'Cl₂',
    rel: '共依存 🌀', exp: '重い者同士の結合。周りを寄せ付けない毒気がある。',
    seriousExp: '塩素原子が互いに1個ずつ価電子を出し合い単結合を形成。各塩素原子の周りには3組の非共有電子対が存在します。',
    layout: {
      atoms: [ { sym: 'Cl', x: 30, y: 50, lp: [90, 180, 270] }, { sym: 'Cl', x: 70, y: 50, lp: [90, 0, 270] } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  {
    id: 'HCl', f: {'H':1, 'Cl':1}, score: 50, name: '塩化水素', formula: 'HCl',
    rel: '振り回され愛 😭', exp: '重いClに強く引っ張られ(極性)、Hが涙目になる関係。',
    seriousExp: '共有電子対が電気陰性度の大きい塩素原子の方に引き寄せられるため、分子全体として極性を持ちます。',
    layout: {
      atoms: [ { sym: 'H', x: 30, y: 50 }, { sym: 'Cl', x: 70, y: 50, lp: [90, 0, 270] } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  {
    id: 'HF', f: {'H':1, 'F':1}, score: 60, name: 'フッ化水素', formula: 'HF',
    rel: '絶対服従 ⛓️', exp: '電気陰性度最強のFに完全に支配されるH。',
    seriousExp: 'フッ素の電気陰性度が非常に大きいため、強い極性を持ちます。分子間で水素結合を形成します。',
    layout: {
      atoms: [ { sym: 'H', x: 30, y: 50 }, { sym: 'F', x: 70, y: 50, lp: [90, 0, 270] } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  {
    id: 'HBr', f: {'H':1, 'Br':1}, score: 50, name: '臭化水素', formula: 'HBr',
    rel: '束縛純愛 🔗', exp: '一途なHが束縛系のBrにがっちり捕まる関係。',
    seriousExp: '臭素と水素の化合物。水によく溶け、水溶液は強酸性(臭化水素酸)を示します。',
    layout: {
      atoms: [ { sym: 'H', x: 30, y: 50 }, { sym: 'Br', x: 70, y: 50, lp: [90, 0, 270] } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  {
    id: 'HI', f: {'H':1, 'I':1}, score: 50, name: 'ヨウ化水素', formula: 'HI',
    rel: '理詰め愛 📘', exp: 'インテリのIに論理で縛られながらも付き合う関係。',
    seriousExp: 'ヨウ素と水素の化合物。ハロゲン化水素の中で最も強い酸性を示します。',
    layout: {
      atoms: [ { sym: 'H', x: 30, y: 50 }, { sym: 'I', x: 70, y: 50, lp: [90, 0, 270] } ],
      bonds: [ { s: 0, t: 1, o: 1 } ]
    }
  },
  {
    id: 'O2', f: {'O':2}, score: 50, name: '酸素', formula: 'O₂',
    rel: '濃厚な両想い 💋', exp: 'お互いの手を2本ずつ絡め合う(二重結合)アツい関係。',
    seriousExp: '酸素原子が2個ずつ価電子を出し合い、2組の共有電子対を作って結合（二重結合）しています。',
    layout: {
      atoms: [ { sym: 'O', x: 30, y: 50, lp: [90, 270] }, { sym: 'O', x: 70, y: 50, lp: [90, 270] } ],
      bonds: [ { s: 0, t: 1, o: 2 } ]
    }
  },
  {
    id: 'H2O', f: {'H':2, 'O':1}, score: 60, name: '水', formula: 'H₂O',
    rel: '堂々たる二股 ✌️', exp: 'Oが2人のHを侍らせる。生命の源だから許される二股。',
    seriousExp: '酸素原子の非共有電子対の反発により折れ線型の構造をとります。極性分子であり、水素結合を形成します。',
    layout: {
      atoms: [ { sym: 'O', x: 50, y: 50, lp: [0, 270] }, { sym: 'H', x: 20, y: 50 }, { sym: 'H', x: 50, y: 80 } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 } ]
    }
  },
  {
    id: 'H2S', f: {'H':2, 'S':1}, score: 70, name: '硫化水素', formula: 'H₂S',
    rel: '秘密の二股 🤫', exp: 'Sによる裏の二股。温泉の匂い(腐卵臭)でバレバレ。',
    seriousExp: '水と同じく折れ線型の構造をとりますが、極性は水よりも小さくなります。腐卵臭を持つ有毒な気体です。',
    layout: {
      atoms: [ { sym: 'S', x: 50, y: 50, lp: [0, 270] }, { sym: 'H', x: 20, y: 50 }, { sym: 'H', x: 50, y: 80 } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 } ]
    }
  },
  {
    id: 'N2', f: {'N':2}, score: 80, name: '窒素', formula: 'N₂',
    rel: '運命の絆 💍', exp: '手を3本も結び合う(三重結合)絶対に離れない2人。',
    seriousExp: '窒素原子が3個ずつ価電子を出し合い、3組の共有電子対を作る（三重結合）。結合エネルギーが大きく非常に安定です。',
    layout: {
      atoms: [ { sym: 'N', x: 30, y: 50, lp: [180] }, { sym: 'N', x: 70, y: 50, lp: [0] } ],
      bonds: [ { s: 0, t: 1, o: 3 } ]
    }
  },
  {
    id: 'NH3', f: {'N':1, 'H':3}, score: 120, name: 'アンモニア', formula: 'NH₃',
    rel: '危険な三股 😈', exp: 'Nが3人のHをキープ。刺激的でちょっとツンとくる関係。',
    seriousExp: '窒素原子に非共有電子対が1組あり、立体構造は三角錐型となります。極性が強く、水によく溶けます。',
    layout: {
      atoms: [ { sym: 'N', x: 50, y: 50, lp: [270] }, { sym: 'H', x: 20, y: 50 }, { sym: 'H', x: 50, y: 80 }, { sym: 'H', x: 80, y: 50 } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 } ]
    }
  },
  {
    id: 'PCl3', f: {'P':1, 'Cl':3}, score: 150, name: '三塩化リン', formula: 'PCl₃',
    rel: 'ドロドロ愛憎劇 💥', exp: '熱血のPが、地雷系のClを3人も囲う超カオスな関係。',
    seriousExp: 'アンモニアと同じく非共有電子対を1組持ち、三角錐型の構造をとります。刺激臭のある有毒な液体です。',
    layout: {
      atoms: [ 
        { sym: 'P', x: 50, y: 50, lp: [270] }, 
        { sym: 'Cl', x: 20, y: 50, lp: [90, 180, 270] }, 
        { sym: 'Cl', x: 50, y: 80, lp: [0, 90, 180] }, 
        { sym: 'Cl', x: 80, y: 50, lp: [0, 90, 270] } 
      ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 } ]
    }
  },
  {
    id: 'BF3', f: {'B':1, 'F':3}, score: 140, name: '三フッ化ホウ素', formula: 'BF₃',
    rel: '絶対零度の関係 ❄️', exp: '不思議なBが最強のFを3人も従える(実は電子不足)。',
    seriousExp: 'ホウ素は価電子が3個のためオクテット則を満たさず、正三角形の平面構造をとる無極性分子です。',
    layout: {
      atoms: [ { sym: 'B', x: 50, y: 50 }, { sym: 'F', x: 50, y: 15, lp: [0, 180, 270] }, { sym: 'F', x: 20, y: 70, lp: [120, 180, 240] }, { sym: 'F', x: 80, y: 70, lp: [300, 0, 60] } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 } ]
    }
  },
  {
    id: 'CO2', f: {'C':1, 'O':2}, score: 100, name: '二酸化炭素', formula: 'CO₂',
    rel: '両手に愛人 🍷', exp: 'Cが2人のOと二重結合。一直線(直線型)でバレバレの浮気。',
    seriousExp: '炭素原子が2つの酸素原子とそれぞれ二重結合を形成。直線型の構造をとるため、極性が打ち消し合い無極性分子となります。',
    layout: {
      atoms: [ { sym: 'O', x: 20, y: 50, lp: [90, 270] }, { sym: 'C', x: 50, y: 50 }, { sym: 'O', x: 80, y: 50, lp: [90, 270] } ],
      bonds: [ { s: 0, t: 1, o: 2 }, { s: 1, t: 2, o: 2 } ]
    }
  },
  {
    id: 'CS2', f: {'C':1, 'S':2}, score: 120, name: '二硫化炭素', formula: 'CS₂',
    rel: '裏の愛人たち 🕶️', exp: 'Cが匂わせのSを両手に抱える。有毒で危険な関係。',
    seriousExp: '二酸化炭素と同様に直線型の無極性分子。常温で揮発性の高い液体で、引火しやすく有毒です。',
    layout: {
      atoms: [ { sym: 'S', x: 20, y: 50, lp: [90, 270] }, { sym: 'C', x: 50, y: 50 }, { sym: 'S', x: 80, y: 50, lp: [90, 270] } ],
      bonds: [ { s: 0, t: 1, o: 2 }, { s: 1, t: 2, o: 2 } ]
    }
  },
  {
    id: 'CH4', f: {'C':1, 'H':4}, score: 150, name: 'メタン', formula: 'CH₄',
    rel: 'ハーレム王 🏰', exp: 'Cの周りに4人の一途なH。正四面体という完璧なバランス。',
    seriousExp: '炭素の4つの価電子がすべて結合に使われ、反発が最小になる正四面体型の立体構造をとる無極性分子です。',
    layout: {
      atoms: [ { sym: 'C', x: 50, y: 50 }, { sym: 'H', x: 50, y: 20 }, { sym: 'H', x: 50, y: 80 }, { sym: 'H', x: 20, y: 50 }, { sym: 'H', x: 80, y: 50 } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 }, { s: 0, t: 4, o: 1 } ]
    }
  },
  {
    id: 'SiH4', f: {'Si':1, 'H':4}, score: 160, name: 'シラン', formula: 'SiH₄',
    rel: 'デジタルハーレム 📱', exp: 'IT系のSiがHを4人集めて構築したネットワーク。',
    seriousExp: 'メタンと同様に正四面体型の構造をもちます。ケイ素は炭素と同族（14族）であるため性質が似ています。',
    layout: {
      atoms: [ { sym: 'Si', x: 50, y: 50 }, { sym: 'H', x: 50, y: 20 }, { sym: 'H', x: 50, y: 80 }, { sym: 'H', x: 20, y: 50 }, { sym: 'H', x: 80, y: 50 } ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 }, { s: 0, t: 4, o: 1 } ]
    }
  },
  {
    id: 'CCl4', f: {'C':1, 'Cl':4}, score: 200, name: '四塩化炭素', formula: 'CCl₄',
    rel: '修羅場ハーレム 🗡️', exp: '重いClを4人も囲う命知らずのC。もはや逃げ場はない。',
    seriousExp: 'メタンの水素がすべて塩素に置換された形。正四面体構造のため、C-Cl結合は極性を持つが分子全体では無極性になります。',
    layout: {
      atoms: [ 
        { sym: 'C', x: 50, y: 50 }, 
        { sym: 'Cl', x: 50, y: 20, lp: [0, 180, 270] }, 
        { sym: 'Cl', x: 50, y: 80, lp: [0, 90, 180] }, 
        { sym: 'Cl', x: 20, y: 50, lp: [90, 180, 270] }, 
        { sym: 'Cl', x: 80, y: 50, lp: [0, 90, 270] } 
      ],
      bonds: [ { s: 0, t: 1, o: 1 }, { s: 0, t: 2, o: 1 }, { s: 0, t: 3, o: 1 }, { s: 0, t: 4, o: 1 } ]
    }
  }
];

const GAME_TIME = 90; 

type GameState = 'title' | 'ready' | 'playing' | 'recipes' | 'result';

type ChemistryRomancePuzzleProps = {
  initialGameState?: GameState;
};

const ScreenFooter = ({ onBack }) => (
  <footer
    data-testid="screen-footer"
    className="screen-footer shrink-0 -mx-4 sm:-mx-6 mt-3 px-4 sm:px-6 pt-3 bg-[#fdfaf6] border-t border-slate-200 shadow-[0_-8px_20px_rgba(15,23,42,0.08)] z-40"
  >
    <button
      type="button"
      onClick={onBack}
      className="w-full min-h-12 py-3 bg-slate-800 hover:bg-slate-700 active:bg-slate-900 text-white rounded-full font-bold text-lg shadow-lg transition-colors"
    >
      タイトルに戻る
    </button>
  </footer>
);

export default function ChemistryRomancePuzzle({
  initialGameState = 'title',
}: ChemistryRomancePuzzleProps) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  
  const [gameState, setGameState] = useState<GameState>(initialGameState); 
  const [showStart, setShowStart] = useState(false); 
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [madeMolecules, setMadeMolecules] = useState([]); 
  const [popups, setPopups] = useState([]); 
  
  const engineRef = useRef({ balls: [], particles: [], tick: 0 });
  const pathRef = useRef([]);
  const tapRef = useRef({ id: null, time: 0 });

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateAppHeight = () => {
      const height = viewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        '--app-height',
        `${Math.round(height)}px`,
      );
    };

    updateAppHeight();
    window.addEventListener('resize', updateAppHeight);
    window.addEventListener('orientationchange', updateAppHeight);
    viewport?.addEventListener('resize', updateAppHeight);
    viewport?.addEventListener('scroll', updateAppHeight);

    return () => {
      window.removeEventListener('resize', updateAppHeight);
      window.removeEventListener('orientationchange', updateAppHeight);
      viewport?.removeEventListener('resize', updateAppHeight);
      viewport?.removeEventListener('scroll', updateAppHeight);
      document.documentElement.style.removeProperty('--app-height');
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => {
      setSize({ w: entries[0].contentRect.width, h: entries[0].contentRect.height });
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (gameState === 'ready' && size.w > 0) {
      engineRef.current.balls = [];
      for (let i = 0; i < 80; i++) addBall(size.w, size.h);
      for (let i = 0; i < 30; i++) updatePhysics(size.w, size.h);

      const timer = setTimeout(() => {
        setGameState('playing');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameState, size]);

  useEffect(() => {
    if (gameState === 'playing') {
      setShowStart(true);
      const timer = setTimeout(() => setShowStart(false), 1200);
      return () => clearTimeout(timer);
    }
  }, [gameState]);

  useEffect(() => {
    if ((gameState !== 'playing' && gameState !== 'ready') || size.w === 0) return;
    
    let frameId;
    let previousFrameTime;
    const ctx = canvasRef.current.getContext('2d');
    const eng = engineRef.current;

    const loop = (frameTime) => {
      const dt = previousFrameTime === undefined
        ? 1
        : getFrameDelta(frameTime - previousFrameTime);
      previousFrameTime = frameTime;

      updatePhysics(size.w, size.h, dt);
      draw(ctx, size.w, size.h);
      eng.tick += dt;
      
      if (gameState === 'playing' && eng.balls.length < 80 && eng.tick >= 15) {
        addBall(size.w, size.h, true);
        eng.tick = 0;
      }

      frameId = requestAnimationFrame(loop);
    };
    frameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameId);
  }, [gameState, size]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameState('result');
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [gameState]);

  const showPopup = (text) => {
    const id = Date.now() + Math.random();
    setPopups(prev => [...prev, { id, text }]);
    setTimeout(() => {
      setPopups(prev => prev.filter(p => p.id !== id));
    }, 1500);
  };

  const addBall = (width, height, isEntering = false) => {
    const symKey = DROP_POOL[Math.floor(Math.random() * DROP_POOL.length)];
    const def = ATOMS[symKey];
    const motion = getAtomMotionProfile(width);
    engineRef.current.balls.push({
      id: `b_${Date.now()}_${Math.random()}`,
      symKey, ...def,
      x: isEntering ? (Math.random() > 0.5 ? -20 : width + 20) : (Math.random() * (width - 60) + 30), 
      y: isEntering ? (Math.random() > 0.5 ? -20 : height + 20) : (Math.random() * (height - 60) + 30),
      vx: (Math.random() * 2 - 1) * motion.initialVelocity,
      vy: (Math.random() * 2 - 1) * motion.initialVelocity,
      scale: isEntering ? 0.1 : 1 
    });
  };

  const getValidNextSymbols = (pathIds, balls) => {
    if (pathIds.length === 0) return null;
    
    const selectedBalls = pathIds.map(id => balls.find(b => b.id === id)).filter(Boolean);
    const counts = {};
    selectedBalls.forEach(b => { counts[b.symKey] = (counts[b.symKey] || 0) + 1; });

    const validNextSyms = new Set();

    for (let r of RECIPES) {
      let isSubset = true;
      for (let k in counts) {
        if (!r.f[k] || counts[k] > r.f[k]) {
          isSubset = false;
          break;
        }
      }
      if (isSubset) {
        for (let k in r.f) {
          if ((counts[k] || 0) < r.f[k]) {
            validNextSyms.add(k);
          }
        }
      }
    }
    return validNextSyms;
  };

  const updatePhysics = (width, height, dt = 1) => {
    const { balls, particles } = engineRef.current;
    const motion = getAtomMotionProfile(width);
    const damping = Math.pow(0.95, dt);
    const accelerationScale = Math.sqrt(dt);

    for (let i = 0; i < balls.length; i++) {
      let b = balls[i];
      if (b.scale < 1) b.scale = Math.min(1, b.scale + 0.05 * dt);

      b.vx += (Math.random() - 0.5) * motion.randomAcceleration * accelerationScale;
      b.vy += (Math.random() - 0.5) * motion.randomAcceleration * accelerationScale;

      b.vx *= damping;
      b.vy *= damping;

      const limitedVelocity = limitVelocity(b.vx, b.vy, motion.maxVelocity);
      b.vx = limitedVelocity.vx;
      b.vy = limitedVelocity.vy;
      
      b.x += b.vx * dt;
      b.y += b.vy * dt;
    }

    const iterations = 3;
    for (let iter = 0; iter < iterations; iter++) {
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          let b1 = balls[i], b2 = balls[j];
          let dx = b2.x - b1.x, dy = b2.y - b1.y;
          let distSq = dx*dx + dy*dy;
          let minDist = b1.r + b2.r;

          if (distSq < minDist * minDist && distSq > 0) {
            let dist = Math.sqrt(distSq);
            let overlap = minDist - dist;
            let nx = dx / dist, ny = dy / dist;
            
            b1.x -= nx * overlap * 0.5; b1.y -= ny * overlap * 0.5;
            b2.x += nx * overlap * 0.5; b2.y += ny * overlap * 0.5;

            if (iter === iterations - 1) {
              let vn = (b1.vx - b2.vx) * nx + (b1.vy - b2.vy) * ny;
              if (vn < 0) {
                let impulse = -1.2 * vn / 2;
                b1.vx += impulse * nx; b1.vy += impulse * ny;
                b2.vx -= impulse * nx; b2.vy -= impulse * ny;
              }
            }
          } else if (iter === iterations - 1 && distSq < (minDist + 30) * (minDist + 30) && distSq > 0) {
            let dist = Math.sqrt(distSq);
            let force = 2.0 / dist; 
            b1.vx -= (dx / dist) * force; b1.vy -= (dy / dist) * force;
            b2.vx += (dx / dist) * force; b2.vy += (dy / dist) * force;
          }
        }
      }

      for (let i = 0; i < balls.length; i++) {
        let b = balls[i];
        if (b.x < b.r) { b.x = b.r; if(iter===iterations-1) b.vx *= -1; }
        if (b.x > width - b.r) { b.x = width - b.r; if(iter===iterations-1) b.vx *= -1; }
        if (b.y < b.r) { b.y = b.r; if(iter===iterations-1) b.vy *= -1; }
        if (b.y > height - b.r) { b.y = height - b.r; if(iter===iterations-1) b.vy *= -1; }
      }
    }

    for (const b of balls) {
      const limitedVelocity = limitVelocity(b.vx, b.vy, motion.maxVelocity);
      b.vx = limitedVelocity.vx;
      b.vy = limitedVelocity.vy;
    }

    if (particles) {
      for (let i = particles.length - 1; i >= 0; i--) {
        let p = particles[i];
        p.x += p.vx * dt; p.y += p.vy * dt; p.life -= 0.03 * dt; 
        if (p.life <= 0) particles.splice(i, 1);
      }
    }
  };

  const draw = (ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);
    const { balls, particles } = engineRef.current;
    const path = pathRef.current;

    const validNextSyms = path.length > 0 ? getValidNextSymbols(path, balls) : null;

    if (path.length > 0) {
      ctx.beginPath();
      ctx.lineWidth = 10;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(251, 113, 133, 0.8)'; 
      ctx.shadowColor = '#fb7185';
      ctx.shadowBlur = 10;

      let firstBall = balls.find(b => b.id === path[0]);
      if(firstBall) ctx.moveTo(firstBall.x, firstBall.y);
      for (let i = 1; i < path.length; i++) {
        let b = balls.find(b => b.id === path[i]);
        if (b) ctx.lineTo(b.x, b.y);
      }
      ctx.stroke();
      ctx.shadowBlur = 0; 
    }

    if (particles) {
      for (let p of particles) {
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life;
        ctx.font = `${10 + p.life * 15}px sans-serif`;
        ctx.fillText('❤', p.x, p.y);
      }
      ctx.globalAlpha = 1.0;
    }

    for (let b of balls) {
      let isSelected = path.includes(b.id);
      let isDimmed = false;
      let isTarget = false;

      if (path.length > 0 && !isSelected) {
        if (validNextSyms && validNextSyms.has(b.symKey)) {
          isTarget = true;
        } else {
          isDimmed = true; 
        }
      }
      
      ctx.save();
      ctx.translate(b.x, b.y);
      let s = b.scale * (isSelected ? 1.15 : 1.0);
      ctx.scale(s, s);

      ctx.globalAlpha = isDimmed ? 0.3 : 1.0; 

      if (isTarget) {
        ctx.beginPath();
        ctx.arc(0, 0, b.r + 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#fde047'; 
        ctx.stroke();
      }

      ctx.beginPath();
      ctx.arc(0, 0, b.r, 0, Math.PI * 2);
      ctx.fillStyle = b.c;
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = isSelected ? '#fb7185' : b.border; 
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      ctx.fillStyle = b.text;
      ctx.font = `900 ${b.sym.length>2 ? 10 : 12}px "M PLUS Rounded 1c", sans-serif`;
      ctx.fillText(b.sym, 0, -4);
      
      ctx.font = `12px sans-serif`;
      ctx.fillText(b.face, 0, 7);
      
      ctx.restore();
    }
  };

  const getPointerPos = (e) => {
    if (!canvasRef.current) return { x:0, y:0 };
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = e.clientX ?? (e.touches && e.touches[0].clientX);
    const cy = e.clientY ?? (e.touches && e.touches[0].clientY);
    return { x: cx - rect.left, y: cy - rect.top };
  };

  const getHoveredBall = (x, y) => {
    const { balls } = engineRef.current;
    for (let i = balls.length - 1; i >= 0; i--) { 
      let b = balls[i];
      let dx = b.x - x, dy = b.y - y;
      if (dx*dx + dy*dy < (b.r + 5) * (b.r + 5)) return b;
    }
    return null;
  };

  const spawnParticles = (x, y, count = 3, spread = 15) => {
    if (!engineRef.current.particles) engineRef.current.particles = [];
    for(let i=0; i<count; i++) {
      engineRef.current.particles.push({
        x: x + (Math.random()-0.5) * spread,
        y: y + (Math.random()-0.5) * spread,
        vx: (Math.random()-0.5) * 2,
        vy: (Math.random()-0.5) * 2 - 1, 
        life: 1,
        color: `hsl(${Math.random()*20 + 340}, 100%, 65%)` 
      });
    }
  };

  const handleDown = (e) => {
    if (gameState !== 'playing') return;
    e.preventDefault();
    const pos = getPointerPos(e);
    const b = getHoveredBall(pos.x, pos.y);
    const now = Date.now();

    if (b) {
      if (tapRef.current.id === b.id && now - tapRef.current.time < 300) {
        pathRef.current = [];
        tapRef.current = { id: null, time: 0 };
        spawnParticles(b.x, b.y, 5, 30); 
        return;
      }
      tapRef.current = { id: b.id, time: now };

      const path = pathRef.current;
      if (path.length === 0) {
        path.push(b.id);
        spawnParticles(b.x, b.y, 2);
      } else {
        if (!path.includes(b.id)) {
          const validSyms = getValidNextSymbols(path, engineRef.current.balls);
          if (validSyms && validSyms.has(b.symKey)) {
            path.push(b.id);
            spawnParticles(b.x, b.y, 2);
            
            if (checkConnection(path)) {
              pathRef.current = []; 
            }
          }
        }
      }
    } else {
      if (tapRef.current.id === 'bg' && now - tapRef.current.time < 300) {
        pathRef.current = [];
        tapRef.current = { id: null, time: 0 };
      } else {
        tapRef.current = { id: 'bg', time: now };
      }
    }
  };

  const checkConnection = (pathIds) => {
    if (pathIds.length < 2) return false;

    const { balls } = engineRef.current;
    const selectedBalls = pathIds.map(id => balls.find(b => b.id === id)).filter(Boolean);
    if (selectedBalls.length !== pathIds.length) return false; 

    const counts = {};
    selectedBalls.forEach(b => { counts[b.symKey] = (counts[b.symKey] || 0) + 1; });

    let isSuccess = false;
    let earnedScore = 0;
    let matchedRecipe = null;

    for (let r of RECIPES) {
      let match = true;
      const rKeys = Object.keys(r.f);
      if (rKeys.length !== Object.keys(counts).length) match = false;
      for(let k of rKeys) { if(counts[k] !== r.f[k]) match = false; }
      
      if (match) {
        isSuccess = true; 
        matchedRecipe = r;
        earnedScore = r.score;
        break; 
      }
    }

    if (isSuccess) {
      selectedBalls.forEach(b => spawnParticles(b.x, b.y, 10, 40));
      showPopup(matchedRecipe.rel);

      engineRef.current.balls = balls.filter(b => !pathIds.includes(b.id));
      setScore(s => s + earnedScore);
      
      setMadeMolecules(prev => {
        const exist = prev.find(p => p.name === matchedRecipe.name);
        if (exist) {
          return prev.map(p => p.name === matchedRecipe.name ? {...p, count: p.count+1} : p);
        } else {
          return [...prev, { 
            name: matchedRecipe.name, 
            formula: matchedRecipe.formula,
            rel: matchedRecipe.rel, 
            exp: matchedRecipe.exp,
            seriousExp: matchedRecipe.seriousExp,
            layout: matchedRecipe.layout,
            count: 1 
          }];
        }
      });
      return true;
    }
    return false;
  };

  const startGame = () => {
    setScore(0);
    setTimeLeft(GAME_TIME);
    setMadeMolecules([]);
    engineRef.current.balls = [];
    engineRef.current.particles = [];
    engineRef.current.tick = 0;
    pathRef.current = [];
    setGameState('ready');
  };

  const backToTitle = () => {
    setGameState('title');
  };

  const LegendBar = () => (
    <div className="bg-[#1e4b3e] text-white flex flex-wrap justify-center py-2 px-2 gap-x-3 gap-y-2 text-[10px] sm:text-xs font-bold border-b border-white/10 shadow-inner">
      {Object.values(ATOMS).map(a => (
        <div key={a.sym} className="flex items-center gap-1">
          <span className="text-sm sm:text-base">{a.face}</span>
          <span className="opacity-90">{a.sym}: {a.desc}</span>
          <span className="text-[9px] sm:text-[10px] bg-white/20 px-1 rounded opacity-75">手{a.val}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="viewport-shell flex flex-col w-full items-center justify-center bg-slate-900 text-slate-800 overflow-hidden select-none font-sans">
      <div className="flex flex-col h-full w-full max-w-[420px] relative shadow-2xl overflow-hidden bg-[#1e4b3e]">
      
      {/* --- ヘッダー --- */}
      {gameState === 'playing' && (
        <>
          <div className="h-14 bg-white/10 backdrop-blur-md flex items-center justify-between px-6 shadow-sm z-10 relative text-white">
            <div className="flex flex-col">
              <span className="text-[10px] font-bold tracking-wider opacity-70">愛のスコア</span>
              <span className="text-2xl font-black text-pink-300 leading-none">{score.toLocaleString()}</span>
            </div>
            
            <div className="absolute left-1/2 transform -translate-x-1/2 flex flex-col items-center">
              <span className="text-[10px] font-bold opacity-70">放課後まで</span>
              <span className={`text-3xl font-black leading-none ${timeLeft <= 10 ? 'text-red-400 animate-pulse' : 'text-white'}`}>
                {timeLeft}
              </span>
            </div>
            <div className="w-16"></div> 
          </div>
          <LegendBar />
        </>
      )}

      {/* --- メインエリア --- */}
      <div className="flex-1 relative touch-none overflow-hidden" ref={containerRef}>
        
        <div className="absolute inset-0 pointer-events-none opacity-10"
          style={{ backgroundImage: 'radial-gradient(#ffffff 1.5px, transparent 1.5px)', backgroundSize: '16px 16px' }} />

        {gameState === 'playing' && pathRef.current.length > 0 && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-white/50 text-xs font-bold pointer-events-none text-center">
            光っている相手をタップ！<br/>ダブルタップでキャンセル
          </div>
        )}

        {(gameState === 'playing' || gameState === 'ready') && (
          <canvas
            ref={canvasRef}
            width={size.w} height={size.h}
            className="absolute inset-0 touch-none"
            onPointerDown={handleDown}
          />
        )}

        {popups.map(p => (
          <div key={p.id} className="absolute inset-0 flex items-center justify-center pointer-events-none z-50 animate-in fade-in zoom-in slide-out-up duration-1000 fill-mode-forwards">
            <div className="text-2xl sm:text-3xl font-black text-white bg-pink-500/90 backdrop-blur-sm px-6 py-3 rounded-full drop-shadow-xl transform -rotate-6 border-4 border-white whitespace-nowrap">
              {p.text}
            </div>
          </div>
        ))}

        {gameState === 'ready' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 bg-black/30 backdrop-blur-sm">
            <div className="text-4xl sm:text-5xl font-black text-white drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)] text-center tracking-widest">
              キーンコーン<br/>カーンコーン...
            </div>
          </div>
        )}

        {showStart && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 animate-out fade-out zoom-out duration-1000">
            <div className="text-6xl sm:text-7xl font-black text-pink-400 drop-shadow-[0_4px_10px_rgba(255,255,255,0.8)] transform rotate-12 bg-white/20 px-8 py-4 rounded-3xl backdrop-blur-md">
              恋愛解禁!!
            </div>
          </div>
        )}

        {/* --- タイトル画面 --- */}
        {gameState === 'title' && (
          <div className="absolute inset-0 flex flex-col items-center justify-start pt-12 pb-6 px-6 bg-[#1e4b3e]/95 backdrop-blur-md z-30 text-white overflow-y-auto">
            <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center shadow-xl mb-4 transform -rotate-6 border-2 border-white/20 shrink-0">
              <span className="text-4xl">🏫</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-center drop-shadow-md mb-1 text-pink-300 leading-tight shrink-0">
              私立 共有結合 学園
            </h1>
            <h2 className="text-sm font-bold text-white/80 mb-6 bg-white/10 px-4 py-1 rounded-full shrink-0">〜ドロドロ恋愛パズル〜</h2>
            
            <div className="w-full bg-white/10 p-4 rounded-2xl border border-white/20 shadow-sm mb-6 shrink-0">
              <h3 className="text-center font-bold mb-3 text-pink-200 text-sm">【登場人物】</h3>
              <div className="grid grid-cols-2 gap-2 text-xs font-bold">
                {Object.values(ATOMS).map(a => (
                  <div key={a.sym} className="flex items-center gap-2 bg-black/20 p-2 rounded-lg">
                    <span className="text-xl">{a.face}</span>
                    <div className="flex flex-col">
                      <span className="text-pink-100">{a.sym} ({a.val}本の手)</span>
                      <span className="opacity-80">{a.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-col gap-3 w-full max-w-xs shrink-0 mb-8">
              <button 
                onClick={startGame}
                className="w-full py-4 bg-pink-500 hover:bg-pink-400 text-white rounded-full font-black text-xl shadow-[0_6px_0_#be185d] active:shadow-[0_0px_0_#be185d] active:translate-y-[6px] transition-all"
              >
                カップル成立へ！ 💘
              </button>
              <button 
                onClick={() => setGameState('recipes')}
                className="w-full py-3 bg-white/20 hover:bg-white/30 text-white rounded-full font-bold text-lg border border-white/30 backdrop-blur-sm transition-all"
              >
                相性一覧を見る 📖
              </button>
              <a
                href={`${import.meta.env.BASE_URL}about.html`}
                className="text-center text-sm font-bold text-white/75 underline underline-offset-4 hover:text-white"
              >
                遊び方・学習内容
              </a>
            </div>
          </div>
        )}

        {/* --- 相性一覧(図鑑)画面 --- */}
        {gameState === 'recipes' && (
          <div className="safe-area-top absolute inset-0 min-h-0 max-h-full bg-[#fdfaf6] flex flex-col px-4 sm:px-6 pb-0 overflow-hidden animate-in fade-in zoom-in duration-300 z-30">
            
            <div className="text-center mb-4 shrink-0 pt-2">
              <h2 className="text-lg font-bold text-slate-400 mb-1">図鑑</h2>
              <div className="text-3xl font-black text-pink-500 drop-shadow-sm">
                相性一覧
              </div>
            </div>

            <div className="bg-white flex-1 min-h-0 rounded-3xl border-2 border-slate-200 shadow-inner p-3 sm:p-4 overflow-y-auto relative" style={{overscrollBehavior: 'contain'}}>
              <h3 className="text-center font-bold text-slate-600 mb-4 sticky top-0 bg-white/95 backdrop-blur-sm py-2 z-10 border-b border-slate-100">
                📖 結ばれる運命の組み合わせ
              </h3>
              
              <div className="flex flex-col gap-4 pb-4">
                {RECIPES.map((m, i) => (
                  <div key={i} className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col gap-3 shadow-sm">
                    
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] sm:text-xs font-bold px-2 py-1 rounded-md bg-pink-100 text-pink-600 shrink-0">
                            {m.rel}
                          </span>
                          <span className="font-bold text-slate-700 text-base sm:text-lg">{m.name} <span className="text-slate-400">({m.formula})</span></span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed bg-white p-2 rounded-lg border border-slate-100">
                        {m.exp}
                      </p>
                    </div>

                    <div className="border-t-2 border-dashed border-slate-200 pt-3 mt-1">
                      <h4 className="text-[11px] sm:text-xs font-bold text-indigo-500 mb-2 flex items-center gap-1">
                        <span className="text-sm">🧪</span> 化学的な解説
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] flex flex-col">
                          <div className="text-[10px] text-slate-400 font-bold mb-1 text-center bg-slate-50 rounded py-0.5">電子式 (ルイス構造)</div>
                          <div className="flex-1 flex items-center justify-center min-h-[80px]">
                            <MolecularRenderer layout={m.layout} type="electronDot" />
                          </div>
                        </div>
                        <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] flex flex-col">
                          <div className="text-[10px] text-slate-400 font-bold mb-1 text-center bg-slate-50 rounded py-0.5">構造式</div>
                          <div className="flex-1 flex items-center justify-center min-h-[80px]">
                            <MolecularRenderer layout={m.layout} type="structural" />
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed font-medium">
                        {m.seriousExp}
                      </p>
                    </div>
                    
                  </div>
                ))}
              </div>
            </div>

            <ScreenFooter onBack={backToTitle} />
            
          </div>
        )}

        {/* --- リザルト画面 (SVG図解対応版) --- */}
        {gameState === 'result' && (
          <div className="safe-area-top absolute inset-0 min-h-0 max-h-full bg-[#fdfaf6] flex flex-col px-4 sm:px-6 pb-0 overflow-hidden animate-in fade-in zoom-in duration-300 z-30">
            
            <div className="text-center mb-4 shrink-0 pt-2">
              <h2 className="text-lg font-bold text-slate-400 mb-1">放課後終了！</h2>
              <div className="text-4xl font-black text-pink-500 drop-shadow-sm">
                愛のスコア: {score.toLocaleString()}
              </div>
            </div>

            <div className="bg-white flex-1 min-h-0 rounded-3xl border-2 border-slate-200 shadow-inner p-3 sm:p-4 overflow-y-auto relative" style={{overscrollBehavior: 'contain'}}>
              <h3 className="text-center font-bold text-slate-600 mb-4 sticky top-0 bg-white/95 backdrop-blur-sm py-2 z-10 border-b border-slate-100">
                📝 学級日誌 (本日の恋愛模様)
              </h3>
              
              {madeMolecules.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400 font-bold text-center">
                  <span className="text-5xl mb-3">🍂</span>
                  誰も結ばれませんでした...<br/>明日はがんばろう。
                </div>
              ) : (
                <div className="flex flex-col gap-4 pb-4">
                  {madeMolecules.sort((a,b)=>b.count-a.count).map((m, i) => (
                    <div key={i} className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col gap-3 shadow-sm">
                      
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] sm:text-xs font-bold px-2 py-1 rounded-md bg-pink-100 text-pink-600 shrink-0">
                              {m.rel}
                            </span>
                            <span className="font-bold text-slate-700 text-base sm:text-lg">{m.name} <span className="text-slate-400">({m.formula})</span></span>
                          </div>
                          <span className="text-sm font-black text-slate-400 bg-white px-2 py-1 rounded-lg border border-slate-100">x{m.count}</span>
                        </div>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed bg-white p-2 rounded-lg border border-slate-100">
                          {m.exp}
                        </p>
                      </div>

                      {/* SVG図解レンダリングエリア */}
                      <div className="border-t-2 border-dashed border-slate-200 pt-3 mt-1">
                        <h4 className="text-[11px] sm:text-xs font-bold text-indigo-500 mb-2 flex items-center gap-1">
                          <span className="text-sm">🧪</span> 化学的な解説
                        </h4>
                        
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] flex flex-col">
                            <div className="text-[10px] text-slate-400 font-bold mb-1 text-center bg-slate-50 rounded py-0.5">電子式 (ルイス構造)</div>
                            <div className="flex-1 flex items-center justify-center min-h-[80px]">
                              <MolecularRenderer layout={m.layout} type="electronDot" />
                            </div>
                          </div>
                          <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] flex flex-col">
                            <div className="text-[10px] text-slate-400 font-bold mb-1 text-center bg-slate-50 rounded py-0.5">構造式</div>
                            <div className="flex-1 flex items-center justify-center min-h-[80px]">
                              <MolecularRenderer layout={m.layout} type="structural" />
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-[11px] sm:text-xs text-slate-600 leading-relaxed font-medium">
                          {m.seriousExp}
                        </p>
                      </div>
                      
                    </div>
                  ))}
                </div>
              )}
            </div>

            <ScreenFooter onBack={backToTitle} />
            
          </div>
        )}
        
      </div>
      </div>
      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />
    </div>
  );
}
